import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ColumnFiltersState } from '@tanstack/react-table';
import groupBy from 'lodash/groupBy';
import {
  BarChart3,
  Bot,
  ChevronDown,
  FolderKanban,
  Fullscreen,
  OctagonX,
  ScanText,
  Settings,
  ShieldCheck,
  UserPlus2,
} from 'lucide-react';
import z from 'zod';

import { useCancelAgenticRunMutation, useStartAgenticRunMutation } from '@/api/endpoints/agentic-remediation';
import { useRemedyV3DetailQuery } from '@/api/endpoints/remedy';
import { Button } from '@/components/ui/base/button';
import { ButtonGroup } from '@/components/ui/base/button-group';
import { useUserSession } from '@/context/UserSessionContext';
import { AgenticRunsSummary } from '@/features/code-remedy/agentic/AgenticRunsSummary';
import { BulkAgenticStartDialog } from '@/features/code-remedy/agentic/BulkAgenticStartDialog';
import { AssignObjectModal } from '@/features/code-remedy/AssignObjectModal';
import { BulkTransportRequestUploadModal } from '@/features/code-remedy/BulkTransportRequestUpload';
import { CodeRemedyObjectsTable } from '@/features/code-remedy/CodeRemedyObjectsTable';
import { GenerateSummaryButton } from '@/features/code-remedy/GenerateSummaryButton';
import { useCodeRemedyFlags } from '@/features/code-remedy/hooks/use-code-remedy-flags';
import { useRemediationRuleIds } from '@/features/code-remedy/hooks/use-remediation-rule-ids';
import { useRemedyAgenticRuns } from '@/features/code-remedy/hooks/use-remedy-agentic-runs';
import { useRemedyDisplayName } from '@/features/code-remedy/hooks/use-remedy-display-name';
import { useRemedyObjectBulkActions } from '@/features/code-remedy/hooks/use-remedy-object-bulk-actions';
import { RemediationAnalysisButton } from '@/features/code-remedy/RemediationAnalysisButton';
import { RemedyStatusBadge } from '@/features/code-remedy/RemedyStatusBadge';
import { CodeRemedyObjectTab } from '@/features/code-remedy/types';
import { columnFiltersFromSearch, columnFiltersToSearchParams } from '@/features/code-remedy/url-state';
import { useIsoLayoutEffect } from '@/hooks/use-iso-layout-effect';
import { useModal } from '@/hooks/use-modal';
import { Badge } from '@/modus/Badge';
import { DropdownMenu } from '@/modus/DropdownMenu';
import { toast } from '@/modus/Toast';
import type { TLAIRemedyV3ObjectDetail } from '@/types/api';
import { TLAIRemedyObjectStatus, TLAIRemedyStatus } from '@/types/api';
import { InputNext } from '@/ui/InputNext';
import { PanelTabs } from '@/ui/panel-tabs/PanelTabs';
import { SkeletonLoader } from '@/ui/skeleton/SkeletonLoader';
import { isNotNullOrUndefined } from '@/utils/filter';
import { updateUrlParamsWithoutRefresh } from '@/utils/url';

// Pre-rename ?tab= value → current name, for old bookmarks / the redirect.
const LEGACY_OBJECT_TAB: Record<string, CodeRemedyObjectTab> = {
  'Ready for Review': CodeRemedyObjectTab.READY_FOR_REVIEW,
};

export const Route = createFileRoute('/_protected/t/$tenantId/code-modernization/$remedyId/')({
  component: CodeRemedyDetailRoute,
  validateSearch: z.object({
    tab: z
      .preprocess((v) => (typeof v === 'string' ? (LEGACY_OBJECT_TAB[v] ?? v) : v), z.nativeEnum(CodeRemedyObjectTab))
      .optional()
      .catch(undefined),
    search: z.string().optional().catch(undefined),
    status: z
      .union([z.string(), z.array(z.string())])
      .optional()
      .catch(undefined),
    object_type: z
      .union([z.string(), z.array(z.string())])
      .optional()
      .catch(undefined),
    assigned_to: z
      .union([z.string(), z.array(z.string())])
      .optional()
      .catch(undefined),
    assigned_by: z
      .union([z.string(), z.array(z.string())])
      .optional()
      .catch(undefined),
    transport_request: z
      .union([z.string(), z.array(z.string())])
      .optional()
      .catch(undefined),
  }),
});

const CodeRemedyObjectTabs = PanelTabs<CodeRemedyObjectTab>;

function CodeRemedyDetailRoute() {
  const { remedyId, tenantId } = Route.useParams();
  const navigate = useNavigate();
  const {
    tab: tabParam,
    search: searchParam,
    status: statusFilter,
    object_type: objectTypeFilter,
    assigned_to: assignedToFilter,
    assigned_by: assignedByFilter,
    transport_request: transportRequestFilter,
  } = Route.useSearch();

  const { user, hasRole } = useUserSession();
  const { enableRemediationAnalysis, enableRemediationReport } = useCodeRemedyFlags();
  const { data: agenticRunsData, isError: isAgenticRunsError } = useRemedyAgenticRuns(remedyId);
  const ruleIds = useRemediationRuleIds(remedyId);

  const [startAgenticRun] = useStartAgenticRunMutation();
  const [cancelAgenticRun] = useCancelAgenticRunMutation();

  const handleAgenticStart = useCallback(
    async (objectId: string, _subObjectIds: string[]) => {
      if (ruleIds === undefined) {
        toast.info('Curated rule sets are still loading — try again in a moment.');
        return;
      }
      await startAgenticRun({
        remedy_id: remedyId,
        object_ids: [objectId],
        ...(ruleIds.length > 0 ? { rule_ids: ruleIds } : {}),
      });
    },
    [startAgenticRun, remedyId, ruleIds],
  );

  const handleAgenticCancel = useCallback(
    async (runId: string) => {
      await cancelAgenticRun({ runId, remedyId });
    },
    [cancelAgenticRun, remedyId],
  );

  const { open, handleOpenChange, handleOpen } = useModal();
  const {
    open: bulkTRModalOpen,
    handleOpenChange: handleBulkTRModalOpenChange,
    handleOpen: handleBulkTRModalOpen,
  } = useModal();
  const {
    open: bulkAgenticOpen,
    handleOpenChange: handleBulkAgenticOpenChange,
    handleOpen: handleBulkAgenticOpen,
  } = useModal();
  const [tab, setTab] = useState(CodeRemedyObjectTab.ASSIGNED_TO_ME);
  const [search, setSearch] = useState(searchParam ?? '');
  const derivedColumnFilters = useMemo(
    () =>
      columnFiltersFromSearch({
        status: statusFilter,
        object_type: objectTypeFilter,
        assigned_to: assignedToFilter,
        assigned_by: assignedByFilter,
        transport_request: transportRequestFilter,
      }),
    [statusFilter, objectTypeFilter, assignedToFilter, assignedByFilter, transportRequestFilter],
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(derivedColumnFilters);

  useEffect(() => {
    setSearch(searchParam ?? '');
  }, [searchParam]);

  useEffect(() => {
    setColumnFilters(derivedColumnFilters);
  }, [derivedColumnFilters]);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const next = event.target.value;
    setSearch(next);

    updateUrlParamsWithoutRefresh({
      search: next.trim() ? next : undefined,
    });
  }, []);

  const handleColumnFiltersChange = useCallback((next: ColumnFiltersState) => {
    setColumnFilters(next);
    const params = columnFiltersToSearchParams(next);
    updateUrlParamsWithoutRefresh(params);
  }, []);

  const { data: remedy, isLoading: isLoadingRemedy } = useRemedyV3DetailQuery(
    { remedy_id: remedyId },
    {
      refetchInterval: ({ state: { data } }) => {
        if (
          data?.status === TLAIRemedyStatus.INITIAL_SCAN_IN_PROGRESS ||
          data?.status === TLAIRemedyStatus.SETUP_IN_PROGRESS
        ) {
          return 2000;
        }
        return false;
      },
    },
  );

  // Never `remedy.name` in the heading: for a Code Modernization project that is the generated
  // `cm-<project_id>` key (MOS-2348), which is what this page's *own* title showed until MOS-2517
  // — the breadcrumb above it had already been fixed. `isResolving` keeps the skeleton up through
  // the project's round-trip rather than flashing the key while it lands.
  const { displayName, isResolving: isResolvingName } = useRemedyDisplayName(remedy);

  const objectParentMap = useMemo(() => {
    return groupBy(remedy?.objects ?? [], ({ parent_id }) => parent_id);
  }, [remedy?.objects]);

  const runsPerObject = useMemo(() => {
    // Without a complete run picture, per-object run state (and the bulk-start bucketing
    // built on it) would misreport "never run" and invite duplicate runs — hide it instead.
    if (isAgenticRunsError) return undefined;
    // Runs can land on sub-object ids (object-page starts on a selected sub-object,
    // TR-level triggers); fold them into the parent row so the table cells and the
    // bulk-start buckets see unit-scoped state.
    const parentByObjectId: Record<string, string> = {};
    for (const object of remedy?.objects ?? []) {
      if (object.parent_id) parentByObjectId[object.object_id] = object.parent_id;
    }
    const runs = (agenticRunsData?.runs ?? []).filter((r): r is typeof r & { object_id: string } =>
      isNotNullOrUndefined(r.object_id),
    );
    return groupBy(runs, (r) => parentByObjectId[r.object_id] ?? r.object_id);
  }, [isAgenticRunsError, agenticRunsData?.runs, remedy?.objects]);

  const {
    selectedObjects,
    setSelectedObjects,
    handleScan,
    handleActivate,
    handleAssign,
    handleCancelScans,
    isScanning,
    isAssigning,
    isActivating,
    isCancelling,
  } = useRemedyObjectBulkActions({ remedyId, objectParentMap });

  const handleTabChange = useCallback(
    (newTab: CodeRemedyObjectTab) => {
      if (newTab === tab) return;
      setTab(newTab);
      setSelectedObjects(new Set());
      updateUrlParamsWithoutRefresh({
        tab: newTab,
      });
    },
    [tab, setSelectedObjects],
  );

  const assignedToMeObjects = useMemo(
    () => remedy?.objects.filter(({ assigned_to, parent_id }) => assigned_to === user.email && !parent_id).length,
    [remedy?.objects, user.email],
  );

  const readyForReviewObjects = useMemo(
    () =>
      remedy?.objects.filter(
        ({ assigned_by, parent_id, status }) =>
          assigned_by === user.email && !parent_id && status === TLAIRemedyObjectStatus.READY_FOR_REVIEW,
      ).length,
    [remedy?.objects, user.email],
  );

  useIsoLayoutEffect(() => {
    if (tabParam) {
      setTab(tabParam);
      return;
    }

    if (assignedToMeObjects) {
      setTab(CodeRemedyObjectTab.ASSIGNED_TO_ME);
    } else {
      setTab(CodeRemedyObjectTab.ALL);
    }
  }, [tabParam, assignedToMeObjects]);

  const parentObjects = useMemo(() => {
    const objects =
      remedy?.objects
        .filter(({ assigned_to, assigned_by, status }) =>
          tab === CodeRemedyObjectTab.ASSIGNED_TO_ME
            ? assigned_to === user.email
            : tab === CodeRemedyObjectTab.READY_FOR_REVIEW
              ? assigned_by === user.email && status === TLAIRemedyObjectStatus.READY_FOR_REVIEW
              : true,
        )
        .sort((a, b) => a.name.localeCompare(b.name)) ?? [];

    return (
      objects
        .filter((object) => !object.parent_id)
        .filter((object) => object.name.toLowerCase().includes(search.toLowerCase())) ?? []
    );
  }, [remedy?.objects, search, tab, user.email]);

  const handleBulkAssign = useCallback(
    async (assignee: string) => {
      return handleAssign(assignee, Array.from(selectedObjects));
    },
    [handleAssign, selectedObjects],
  );

  const handleSingleAssign = useCallback(
    async (assignee: string, objectId: TLAIRemedyV3ObjectDetail['object_id']) => {
      return handleAssign(assignee, [objectId]);
    },
    [handleAssign],
  );

  return (
    <div className="flex size-full flex-col gap-6 p-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <FolderKanban className="size-8 stroke-1 text-white" />
          {isLoadingRemedy || !remedy || isResolvingName || !displayName ? (
            <SkeletonLoader className="h-5 max-w-96 rounded-sm" />
          ) : (
            <div className="flex w-full items-center justify-between">
              <h1 className="text-xl font-medium text-white">{displayName}</h1>
              <div className="flex items-center gap-2">
                {/* `remedyName` is a lookup key, not a label: it is POSTed as `{name}` and resolved
                    through `Remedies.name`, so it must stay the raw stored name. The backend
                    derives the human name itself for the job label. */}
                {enableRemediationAnalysis && <RemediationAnalysisButton key={remedyId} remedyName={remedy.name} />}
                {enableRemediationReport && <GenerateSummaryButton key={`summary-${remedyId}`} remedyId={remedyId} />}
                {enableRemediationReport && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Modernization Summary"
                    title="Modernization Summary"
                    className="size-8 text-white/50 hover:text-white"
                    onClick={() =>
                      navigate({
                        to: '/t/$tenantId/code-modernization/$remedyId/report',
                        params: { tenantId, remedyId },
                      })
                    }
                  >
                    <BarChart3 className="size-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Remedy settings"
                  className="size-8 text-white/50 hover:text-white"
                  onClick={() =>
                    navigate({
                      to: '/t/$tenantId/code-modernization/$remedyId/settings',
                      params: { tenantId, remedyId },
                    })
                  }
                >
                  <Settings className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 text-sm">
            {isLoadingRemedy || !remedy ? (
              <SkeletonLoader className="h-3 max-w-96 rounded-sm" />
            ) : (
              <div className="flex w-full items-center gap-2">
                <RemedyStatusBadge status={remedy.status} animate />
              </div>
            )}
          </div>
        </div>
      </div>
      <CodeRemedyObjectTabs tab={tab} onTabChange={handleTabChange}>
        <CodeRemedyObjectTabs.List className="flex w-fit gap-0.5 rounded-lg bg-tlai-3/90 p-1 text-sm">
          <CodeRemedyObjectTabs.Trigger
            disabled={!assignedToMeObjects}
            value={CodeRemedyObjectTab.ASSIGNED_TO_ME}
            className="flex w-fit items-center gap-1 px-3 py-1"
          >
            {CodeRemedyObjectTab.ASSIGNED_TO_ME}
            <Badge variant="outline">{assignedToMeObjects ?? '-'}</Badge>
          </CodeRemedyObjectTabs.Trigger>
          <CodeRemedyObjectTabs.Trigger
            value={CodeRemedyObjectTab.READY_FOR_REVIEW}
            className="w-fit px-3 py-1"
            disabled={!readyForReviewObjects}
          >
            {CodeRemedyObjectTab.READY_FOR_REVIEW}
            <Badge variant="outline">{readyForReviewObjects ?? '-'}</Badge>
          </CodeRemedyObjectTabs.Trigger>
          <CodeRemedyObjectTabs.Trigger value={CodeRemedyObjectTab.ALL} className="w-fit px-3 py-1">
            {CodeRemedyObjectTab.ALL}
          </CodeRemedyObjectTabs.Trigger>
        </CodeRemedyObjectTabs.List>
      </CodeRemedyObjectTabs>
      <div className="flex w-full items-center gap-2">
        <InputNext value={search} onChange={handleSearchChange} placeholder="Search" className="max-w-96" />
        <Button
          variant="outline"
          size="icon"
          aria-label="Upload transport requests"
          title="Upload transport requests"
          className="rounded-lg border-white/10 bg-white/5 hover:bg-white/10"
          disabled={
            !hasRole('cr-lead-access') ||
            remedy?.status === TLAIRemedyStatus.INITIAL_SCAN_IN_PROGRESS ||
            remedy?.status === TLAIRemedyStatus.SETUP_IN_PROGRESS
          }
          onClick={handleBulkTRModalOpen}
        >
          <Fullscreen className="size-4" />
        </Button>
        <ButtonGroup.Root>
          <Button
            variant="outline"
            className="rounded-lg border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2"
            disabled={selectedObjects.size === 0 || isAssigning || !hasRole('cr-lead-access')}
            onClick={handleOpen}
          >
            <UserPlus2 className="size-4" />
            Assign
          </Button>
          <Button
            variant="outline"
            className="rounded-lg border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2"
            disabled={selectedObjects.size === 0 || !hasRole('cr-lead-access') || isScanning || isActivating}
            onClick={handleScan}
          >
            <ScanText className="size-4" />
            Scan
          </Button>
          <Button
            variant="outline"
            className="rounded-lg border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2"
            disabled={selectedObjects.size === 0 || !hasRole('cr-lead-access') || runsPerObject === undefined}
            onClick={handleBulkAgenticOpen}
          >
            <Bot className="size-4" />
            Start AI Runs
          </Button>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger
              disabled={selectedObjects.size === 0 || !hasRole('cr-lead-access')}
              render={(props) => (
                <Button
                  variant="outline"
                  aria-label="More object actions"
                  className="rounded-lg border-white/10 bg-white/5 hover:bg-white/10"
                  {...props}
                >
                  <ChevronDown className="size-4" />
                </Button>
              )}
            />
            <DropdownMenu.Content side="bottom" align="start">
              <DropdownMenu.Item disabled={isActivating || isScanning} onClick={handleActivate}>
                <ShieldCheck className="size-4" />
                Activate
              </DropdownMenu.Item>
              <DropdownMenu.Item disabled={isCancelling} onClick={handleCancelScans}>
                <OctagonX className="size-4" />
                Cancel Scans
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </ButtonGroup.Root>
        <AgenticRunsSummary runs={agenticRunsData?.runs ?? []} className="ml-auto justify-end" />
        <AssignObjectModal
          open={open}
          selectedObjects={selectedObjects}
          onAssign={handleBulkAssign}
          onOpenChange={handleOpenChange}
        />
        <BulkTransportRequestUploadModal
          open={bulkTRModalOpen}
          remedyId={remedyId}
          onOpenChange={handleBulkTRModalOpenChange}
        />
        {runsPerObject !== undefined && (
          <BulkAgenticStartDialog
            open={bulkAgenticOpen}
            onOpenChange={handleBulkAgenticOpenChange}
            remedyId={remedyId}
            selectedObjectIds={Array.from(selectedObjects)}
            runsPerObject={runsPerObject}
            ruleIds={ruleIds}
          />
        )}
      </div>
      <CodeRemedyObjectsTable
        remedyId={remedyId}
        parentObjects={parentObjects}
        selectedObjects={selectedObjects}
        onSelectionChange={setSelectedObjects}
        objectParentMap={objectParentMap}
        isNavigatable={
          remedy?.status === TLAIRemedyStatus.INITIAL_SCAN_COMPLETED ||
          remedy?.status === TLAIRemedyStatus.SETUP_COMPLETED
        }
        isLoading={isLoadingRemedy}
        onAssign={handleSingleAssign}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        runsPerObject={runsPerObject}
        onAgenticStart={handleAgenticStart}
        onAgenticCancel={handleAgenticCancel}
      />
      <span className="flex w-full items-center justify-center text-sm text-white/50">{`${selectedObjects.size} of ${parentObjects.length} object(s) selected`}</span>
    </div>
  );
}
